import { useEnabledFeatures, useQuery, useTeam } from '@vrplatform/data';
import type { SelectItem } from '@vrplatform/components';
import type { accountAssignmentType_enum } from '@vrplatform/graphql';
import {
  formatAssignmentType,
  getAssignmentTypeDescription,
} from '@vrplatform/ui-common';

export function useAccountsAssignmentsQuery({
  currentAccountAssignments,
  skip,
}: {
  skip: boolean;
  currentAccountAssignments: accountAssignmentType_enum[];
}) {
  const [{ id: teamId }] = useTeam();
  const { OPEX } = useEnabledFeatures();

  return useQuery(
    (q, args) => {
      const existingAssignments = q
        .accountAssignments({
          where: {
            tenantId: { _eq: args.teamId },
          },
          order_by: [{ type: 'asc' }],
        })
        .map((assignment) => assignment.type);

      return q
        .accountAssignmentTypes({
          order_by: [{ name: 'asc' }],
          where: args.OPEX
            ? undefined
            : {
                name: { _neq: 'dueToFromTrust' }, // do not require dueToFromTrust for non opex tenants
              },
        })
        .map<SelectItem<accountAssignmentType_enum>>((x) => {
          const type = x.name! as accountAssignmentType_enum;

          const disabled =
            existingAssignments.includes(type) &&
            !args.currentAccountAssignments.includes(type);

          return {
            value: type,
            label: formatAssignmentType(type),
            disabled,
            description: disabled
              ? 'Already assigned to another account'
              : getAssignmentTypeDescription(type),
          };
        });
    },
    {
      skip,
      queryKey: ['accounts', 'assignmentTypes'],
      variables: {
        teamId,
        currentAccountAssignments,
        OPEX,
      },
    }
  );
}
